import { defineNuxtPlugin, useRuntimeConfig } from '#app'

import { createNullCache } from '@algolia/client-common'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { createFetchRequester } from 'algolia-requester-fetch-v5'
import { type SearchClient, algoliasearch } from 'algoliasearch-v5'

import type { ModuleOptions } from '../module'

function createAlgoliaClient(
  config: ModuleOptions,
  marketDomain?: string,
): SearchClient | null {
  const { proxyHost, proxyProtocol = 'https' } = config

  if (proxyHost) {
    const resolvedHost = proxyHost.replaceAll(
      '{market.domain}',
      marketDomain || '',
    )

    const serverOnlyHeaders: Record<string, string> = import.meta.server
      ? {
          'user-agent': 'bm-search-javascript-client',
        }
      : {}

    return algoliasearch('dummy_app_id', 'dummy_key', {
      authMode: 'WithinHeaders',
      baseHeaders: serverOnlyHeaders,
      hosts: [
        {
          accept: 'read' as const,
          protocol: proxyProtocol,
          url: resolvedHost,
        },
      ],
      requester: createFetchRequester({
        requesterOptions: { credentials: 'include' },
      }),
      // Disable Algolia SDK caching - rely on Nuxt's useAsyncData cache instead
      responsesCache: createNullCache(),
      requestsCache: createNullCache(),
      timeouts: {
        // 2 seconds (more generous than default 1s for Cloudflare)
        connect: 2 * 1000,
        // 10 seconds (double default 5s to handle Cloudflare overhead)
        read: 10 * 1000,
        // 30 seconds (keep default for indexing operations)
        write: 30 * 1000,
      },
    })
  }

  return null
}

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig()
  const algoliaConfig = runtimeConfig.public.algolia
  const logger = useLogger()

  if (!algoliaConfig) {
    logger.error(
      '[Algolia] No configuration found. Please configure the module in nuxt.config.ts',
      {
        owners: ['bot-squad-search-and-recommendation-front'],
      },
    )

    return {
      provide: {
        algolia: null,
      },
    }
  }

  const { market } = useMarketplace()
  const marketDomain = market.domain

  const client = createAlgoliaClient(algoliaConfig, marketDomain)

  return {
    provide: {
      algolia: client,
    },
  }
})
